import {
  Injector,
  Input,
  OnDestroy,
  OnInit,
  AfterContentInit,
  ChangeDetectorRef,
  Injectable,
  PLATFORM_ID,
  Directive,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { CasMetadataNode } from '../../../classes/CasMetadataNode';
import { ViewModel } from './ViewModel';
import { Subscription } from 'rxjs';
import { ValidationService } from '../../../services/validation/validation.service';
import { UnsupportedUseCasesService } from '../../../services/unsupportedUseCases/unsupportedUseCases.service';
import { RouteChangeService } from '../../../services/routes/routeChange.service';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { WindowService } from '../../../../../common/angular-shared/services/window/window.service';
import { ApplicationService } from '../../../services/application/application.service';
import { SubjectManager } from '../../../../../common/js/subjectManager/SubjectManager';
import { ApplicationState } from '../../../classes/ApplicationState';
import { ApplicationSectionsList, ApplicationSectionUtils } from '../../../classes/ApplicationSection';
import { ModalService } from '../../../services/modal/modal.service';
import { GeoChangeService } from '../../../services/geoChange/geoChange.service';
import { AMCAuth0Service } from '../../../services/authentication/amc/amc-auth0.service';
import {
  ToastService,
  ToastBySubjectTypes,
} from '../../../../../coverpath-common/angular/components/overlays/toaster/toast-service.service';
import { ToasterIntent } from '../../../../../coverpath-common/angular/components/overlays/toaster/toaster.component';
import { Utils } from '../../../../../common/js/Utils';
import { InformationModalService } from '../../../services/informationModal/informationModal.service';
import { PartnerApplicationFillService } from '../../../../../common/angular-shared/services/application/partner-application-fill.service';
import { PartnerApplicationFillPropertiesMap, ModalType } from '../../../../js/interfaces/CoverpathTypes';
import { ObfuscationProperty } from '../../../../../common/js/interfaces/CommonTypes';
import type { CasApplication } from '../../../../../coverpath-common/classes/coverpath_subclasses/CasApplication';
import { PiiService } from '../../../../../common/angular-shared/services/pii.service';
import { BasicTextInputComponent } from './basic-text-input/basic-text-input.component';

@Directive()
export abstract class BaseFormComponent extends ViewModel implements OnInit, OnDestroy, AfterContentInit {
  @Input() noIndent: boolean;
  @Input() requiredBasedOn: string[];
  customWidth;
  derivedParser;
  derivedFormatter;
  questionItem: CasMetadataNode;
  loopIndex: number = NaN;
  prop: string;
  noLabel: boolean;
  errorMessage: {} = {};
  warningMessage: {} = {};
  showSelf: boolean;
  routeChangeService: RouteChangeService;
  navigationService: NavigationService;
  applicationService: ApplicationService;
  modalService: ModalService;
  geoChangeService: GeoChangeService;
  unsupportedUseCasesService: UnsupportedUseCasesService;
  partnerFillService: PartnerApplicationFillService;
  informationModalService: InformationModalService;
  cdRef: ChangeDetectorRef;
  propertyChangedSubscription: Subscription;
  conditionalDestroyBlock: boolean = false;
  isDisabled: boolean;
  isReadonly: boolean;
  appState: ApplicationState;
  questionAnsweredSubscription: Subscription;
  windowService: WindowService;
  isAdvisor: boolean = false;
  overrideClear: boolean = false;
  isServer: boolean;
  shouldObfuscateInput: boolean;
  obfuscationPattern: string;
  auth0service: AMCAuth0Service;
  validatePreviousSubscription: Subscription;
  isDisabledSubscription: Subscription;
  toastService: ToastService;

  shouldObfuscate: boolean;
  obfuscatePiiSubscription: Subscription;
  piiService: PiiService;
  shouldWaitForPiiFetch: boolean = false;
  obfuscationProperty: ObfuscationProperty;

  private unsupportedUseCaseNotReset: Array<string> = [
    'customer.application.primaryPolicy.insured.person.dob',
    'customer.application.primaryPolicy.faceAmount',
    'customer.application.secondaryPolicy.faceAmount',
    'customer.application.primaryPolicy.termDisplay',
    'customer.application.secondaryPolicy.termDisplay',
    'customer.application.primaryPolicy.hasRtrRiderBool',
    'customer.application.primaryPolicy.hasGirRiderBool',
    'customer.application.primaryPolicy.waiver',
    'customer.application.primaryPolicy.ownerClassification',
    'customer.application.primaryPolicy.insuredType',
  ];

  constructor(protected injector: Injector) {
    super(injector);
    const platformId = this.injector.get(PLATFORM_ID);
    this.isServer = isPlatformServer(platformId);

    this.questionItem = this.injector.get('questionItem');
    this.loopIndex = this.injector.get('loopIndex');
    this.routeChangeService = this.injector.get(RouteChangeService);
    this.navigationService = this.injector.get(NavigationService);
    this.windowService = this.injector.get(WindowService);
    this.applicationService = this.injector.get(ApplicationService);
    this.modalService = this.injector.get(ModalService);
    this.geoChangeService = this.injector.get(GeoChangeService);
    this.unsupportedUseCasesService = this.injector.get(UnsupportedUseCasesService);
    this.partnerFillService = this.injector.get(PartnerApplicationFillService);
    this.toastService = this.injector.get(ToastService);
    this.informationModalService = this.injector.get(InformationModalService);
    this.cdRef = this.injector.get(ChangeDetectorRef);
    this.auth0service = this.injector.get(AMCAuth0Service);
    this.noLabel = this.questionItem.controlType.indexOf('no-label') !== -1;
    this.prop = this.handleIterate(this.questionItem.prop);
    this.appState = this.applicationService.appState;
    this.isAdvisor = this.appState.isAdvisor;
    this.isDisabled = this.getIsDisabled();
    this.shouldObfuscateInput = this.getShouldObfuscateInput();
    this.isReadonly = this.getShouldBeReadOnly();
    this.conditionalDestroyBlock = this.questionItem.conditionalDestroyBlock;
    this.piiService = this.injector.get(PiiService);

    this.setListener();

    this.questionAnsweredSubscription = this.appSvc.questionAnsweredObservable$.subscribe(() => {
      this.questionAnsweredHandler();
      this.isDisabled = this.getIsDisabled();
      this.shouldObfuscateInput = this.getShouldObfuscateInput();
      this.isReadonly = this.getShouldBeReadOnly();
    });

    const validatePreviousSubject = SubjectManager.getSubject('validate_previous');
    this.validatePreviousSubscription = validatePreviousSubject.subscribe((propsToValidate: string[]) => {
      const propStr: string = `${this.questionItem.id}_${this.prop}`;
      if (propsToValidate.includes(propStr)) {
        this.checkEmptyAndValidate(this.prop, this.getValue(this.prop));
      }
    });
    SubjectManager.setSubject('validate_previous', validatePreviousSubject);

    this.obfuscatePiiSubscription = this.appSvc.obfuscatePiiObservable$.subscribe(
      (obfuscationProperty: ObfuscationProperty) => {
        if (this.prop === obfuscationProperty.prop && this.piiService.isPiiProperty(this.prop)) {
          this.shouldObfuscate = obfuscationProperty.shouldObfuscate;
        }
      }
    );
  }

  abstract updateFromModel();

  ngOnInit() {
    this.isDisabled = this.getIsDisabled();
    this.shouldObfuscateInput = this.getShouldObfuscateInput();
    this.isReadonly = this.getShouldBeReadOnly();

    // If the component is given a loop index, bring the max index of that shared node up to match, and update progress
    if (!isNaN(this.loopIndex)) {
      this.questionItem.loopIndex = this.loopIndex;
      this.updateProgressAndSubscriptions();
    }

    this.isDisabledSubscription = this.applicationService.isDisabledObservable$.subscribe(() => {
      this.isDisabled = this.getIsDisabled();
    });
  }

  ngAfterContentInit() {
    if (this.partnerFillService.partnerPropertiesMap) {
      this.setPartnerPrefillData(this.partnerFillService.partnerPropertiesMap);
    }
  }

  setPartnerPrefillData(partnerPropertiesMap: PartnerApplicationFillPropertiesMap) {
    if (partnerPropertiesMap[this.prop] && !this.getValue(this.prop)) {
      this.validateAndSet(this.prop, this.parse(this.prop, partnerPropertiesMap[this.prop]));
    }
  }

  questionAnsweredHandler(): void {
    /**
     * If this question is displayed in the same section as the current section that the user is in
     * then check this question's dataReliesOn list
     *
     * if a prop that is included in this question's dataReliesOn list is included in the subscriptionsInFlight list
     * AND the question is visible
     * then update the view from the model
     */

    const sameSection = ApplicationSectionUtils.isQuestionItemInCurrentSection(
      this.questionItem,
      this.navigationService.currentSection,
      this.appState.controller.customer.getCoverpathFilingType(),
      this.appState
    );

    if (sameSection) {
      this.questionItem.dataReliesOn.forEach((i) => {
        const dataReliesOnStr = i.replace(/\./g, '_') + '_changed';
        if (this.questionItem.subscriptionsInFlight.includes(dataReliesOnStr) && this.questionItem.isShown()) {
          this.updateFromModel();
        }
      });
    }
  }

  getIsDisabled(): boolean {
    if (this.appSvc.appState.controller.isLocked) {
      return true;
    }

    if (this.isServer || this.questionItem.getIsPermanentlyDisabledOnCondition()) {
      return true;
    }

    const isTeleInterviewerLoggedIn: boolean = this.appSvc.appState.isTeleInterviewerLoggedIn;

    const application = this.appSvc.appState.controller.customer.application;
    if (application.isTeleCMI) {
      // this is a tele interview app
      if (application.isTeleCMICompleted) {
        return true;
      }
      const isTeleCMIQuestion: boolean = this.questionItem.isTeleCMIQuestionNode();
      if (isTeleCMIQuestion && !isTeleInterviewerLoggedIn) {
        // tele interview question, but user is not a tele interviewer so disable question
        return true;
      }
    }

    let questionEnabled = this.appSvc.appState.enabledNodesCache[this.questionItem.parentLevel1NodeId];
    if (
      (this.isAdvisor && !this.blockAdvisorOverride()) ||
      (this.questionItem.section === 'Existing Policies' && !this.isQuestionLocked())
    ) {
      questionEnabled = true;
    }
    let conditionalDisable = false; // by default
    if (questionEnabled) {
      conditionalDisable = this.dynamicDisable();
    }
    const enable = questionEnabled && !conditionalDisable; // go consider conditional Disable only if the question is enabled
    return !enable;
  }

  blockAdvisorOverride(): boolean {
    const primaryPolicy = this.appSvc.appState.controller.customer.application.primaryPolicy;
    const flags = {
      mmGiactWebDebitVerificationServiceEnabled:
        this.appSvc.appState.controller.mmGiactWebDebitVerificationServiceEnabled,
    };
    return ApplicationSectionUtils.shouldDisableSection(primaryPolicy, this.questionItem.section, flags);
  }

  dynamicDisable(): boolean {
    // some multi-control questions are disabled depending on the
    // response from another sibling control, not from overall application progress
    // example [Occupation] [x] I am unemployed
    // being unemployed would disable occupation text field
    let conditionallyDisabled = false;
    if (this.requiredBasedOn) {
      switch (this.requiredBasedOn[0]) {
        case 'no':
        case 'yes':
          conditionallyDisabled = false;
          break;
        default: {
          // check the condition to disable this control
          const entity = `controller.customer.application.primaryPolicy.${this.handleIterate(this.requiredBasedOn)}`;
          //tslint:disable-next-line
          conditionallyDisabled = !eval(entity);
          return conditionallyDisabled;
        }
      }
      return conditionallyDisabled;
    }
  }

  /**
   *  Obfuscate input field to hide value
   *      - Make it dynamic like getIsPermanentlyDisabledOnCondition so it return true/false
   *          based on conditions met
   *
   *      - Setting isDisabled as well
   */
  getShouldObfuscateInput(): boolean {
    if (this.windowService.nativeWindow.controller.isHeavyUserEnabled) {
      const conditions: string[] = this.questionItem.obfuscateValueConditional;
      const result: boolean = conditions.some((condition) => {
        switch (condition) {
          case 'advisorAssistedAMCHeavyUser':
            // star out input field if advisor + amc heavy user
            let isOwnerPerson;
            if (this.questionItem.prop.includes('ownerPerson')) {
              isOwnerPerson = true;
            } else if (this.questionItem.prop.includes('otherOwner')) {
              isOwnerPerson = false;
            }
            return this.isAdvisor && this.auth0service.isAMCheavyUser && isOwnerPerson;
          case 'isOwnerSameAsInsuredAdvisorAssistedAMCHeavyUser':
            // star out input field if advisor + amc heavy user + owner is same as insured
            return (
              this.isAdvisor &&
              this.auth0service.isAMCheavyUser &&
              this.appSvc.appState.controller.customer.application.primaryPolicy.isInsuredInfoSameAsOwners()
            );
          default: {
            return condition.toLocaleLowerCase() === 'true' ? true : false;
          }
        }
      });
      return result;
    } else {
      return false;
    }
  }

  isQuestionLocked(): boolean {
    const application = this.appSvc.appState.controller.customer.application;
    return (
      application.lockedByAdvisor &&
      this.appSvc.appState.controller.isQuestionLockEnabled &&
      !this.isAdvisor &&
      this.questionItem.isQuestionLockableNode()
    );
  }

  getShouldBeReadOnly(): boolean {
    if (this.isQuestionLocked()) {
      return true;
    }
    if (this.windowService.nativeWindow.controller.isHeavyUserEnabled) {
      const conditions: string[] = this.questionItem.readonlyConditional;
      const result: boolean = conditions.some((condition) => {
        switch (condition) {
          case 'AMCHeavyUser':
            let isOwnerPerson;
            if (this.questionItem.prop.includes('ownerPerson')) {
              isOwnerPerson = true;
            } else if (this.questionItem.prop.includes('otherOwner')) {
              isOwnerPerson = false;
            }
            return this.auth0service.isAMCheavyUser && isOwnerPerson;
          case 'isOwnerSameAsInsuredAMCHeavyUser':
            // readonly input field if advisor assisted + amc heavy user + owner is same as insured
            return (
              this.auth0service.isAMCheavyUser &&
              this.appSvc.appState.controller.customer.application.primaryPolicy.isInsuredInfoSameAsOwners()
            );
          default: {
            return condition.toLocaleLowerCase() === 'true' ? true : false;
          }
        }
      });
      return result;
    } else {
      return false;
    }
  }

  ngOnDestroy() {
    // Reset reflexive components.
    // Do not reset components when navigating to other pages or other sections
    this.cdRef.detach();

    let questionDestroyedInSection: boolean;

    // if the customer doesn't exist (such as during logout process), no need to do this section logic
    if (this.appState.controller.customer) {
      questionDestroyedInSection = ApplicationSectionUtils.isQuestionItemInCurrentSection(
        this.questionItem,
        this.navigationService.currentSection,
        this.appState.controller.customer.getCoverpathFilingType(),
        this.appState
      );
    }

    // we want to execute the following code if this is *not* part of an iterate-collect set.
    // for the iterate-collect sets, the saveChanges() function is explicitly being called
    // inside removeIterateElement() [application.service.ts]
    if (
      !this.routeChangeService.inNavigation &&
      questionDestroyedInSection &&
      !this.conditionalDestroyBlock &&
      !this.isPartOfIterateCollect()
    ) {
      this.resetNode();
      this.appSvc.saveChanges();
    }

    if (this.propertyChangedSubscription) {
      this.propertyChangedSubscription.unsubscribe();
    }

    if (this.questionAnsweredSubscription) {
      this.questionAnsweredSubscription.unsubscribe();
    }

    if (this.validatePreviousSubscription) {
      this.validatePreviousSubscription.unsubscribe();
    }

    if (this.isDisabledSubscription) {
      this.isDisabledSubscription.unsubscribe();
    }

    this.questionItem.needsFixed = false;

    if (this.obfuscatePiiSubscription) {
      this.obfuscatePiiSubscription.unsubscribe();
    }
  }

  private isPartOfIterateCollect(): boolean {
    return Utils.exists(this.loopIndex) && this.loopIndex >= 0;
  }

  protected resetNode() {
    this.questionItem.resetVal(this.loopIndex);
    this.questionItem.resetChildren(this.loopIndex);
  }

  setVisibility() {
    this.showSelf = this.questionItem.isShown(this.loopIndex);
  }

  setListener() {
    this.propertyChangedSubscription = this.questionItem.propertyChangedObservable$.subscribe(async () => {
      this.showSelf = this.questionItem.isShown(this.loopIndex);

      if (this.showSelf) {
        this.updateFromModel();
        if (this.windowService.nativeWindow.controller.isHeavyUserEnabled) {
          await this.handleAMCdataUpdates(); // this feels dirty but the properties updating are not set in time
        }
        this.appSvc.questionAnsweredSubject.next();
        this.clearErrorMessage(true);
      }
    });
  }

  async handleAMCdataUpdates(): Promise<void> {
    const togglingOwnerAndInsuredRelationship =
      this.questionItem.prop.indexOf('insuredType') !== -1 ||
      this.questionItem.prop.indexOf('ownerClassification') !== -1;
    if (togglingOwnerAndInsuredRelationship) {
      await this.auth0service.handleChangesForHeavyUserProps(this.questionItem.prop);
    }
  }

  clearErrorMessage(clear: boolean): void {
    if (clear && !this.overrideClear) {
      this.errorMessage[this.prop] = ''; //clear error message if disabled
    }
  }

  clearWarningMessage(): void {
    this.warningMessage[this.prop] = '';
  }

  getLevelClass() {
    return `level-${this.noIndent ? '0' : this.questionItem.uiLevel}`;
  }

  addIterateElement(prop: string, arrayType: string, addFuncSuffix: boolean) {
    this.appSvc.addIterateElement(prop, arrayType, addFuncSuffix);
  }

  removeIterateElement(prop: string, ix: number, removeFuncSuffix: boolean) {
    this.appSvc.removeIterateElement(prop, ix, removeFuncSuffix);
    this.appSvc.questionAnsweredSubject.next();
  }

  handleIterate(prop) {
    if (!isNaN(this.loopIndex)) {
      // Replace [i] with loopIndex for iterate controls
      prop = prop.replace('[i]', '[' + this.loopIndex + ']');
    }
    return prop;
  }

  setValue(prop: string, value): void {
    this.appSvc.setValue(prop, value);
  }

  protected parsePredicates(text) {
    if (text) {
      return this.predicateSvc.parseForPredicates(text, this.questionItem.pronounLocked, this.loopIndex);
    }
    return '';
  }

  protected async validateAndSet(prop: string, value): Promise<boolean> {
    const application = this.appSvc.appState.controller.customer.application;
    const primaryPolicy = application.primaryPolicy;

    //If application is in read only mode (isLocked) do not attempt to validate/set.
    if (this.appSvc.appState.controller.isLocked) {
      return false;
    }

    if (
      application.lockedByAdvisor &&
      this.appSvc.appState.controller.isQuestionLockEnabled &&
      !this.isAdvisor &&
      this.questionItem.isQuestionLockableNode()
    ) {
      return false;
    }

    const isEmpty = value === null || value === undefined || value.length === 0;

    if (this.isAdvisor && isEmpty) {
      const parsedValue = this.parse(prop, value);
      this.informationModalService.showMessageIfPresent(prop, parsedValue);
      this.submitValueAndFireTriggers(prop, parsedValue);
      this.updateProgressAndSubscriptions();
      return true;
    }

    if (await this.unsupportedUseCasesService.isUnsupportedUseCase(prop, value)) {
      return this.resetPropForUnsupportedCase(prop);
    }

    /**
     * check for triggering lifebridge info for low income applications
     */
    this.unsupportedUseCasesService.checkOrActivateLifeBridgeMessaging(prop, value);

    /**
     *  for Juvenile Signers
     */
    if (
      prop.match('insured.signer.lastName') &&
      !primaryPolicy.is2020CoverpathOrMMD() &&
      !primaryPolicy.insured.hasSigner
    ) {
      const modalConfig = {
        type: ModalType.signerEmail,
        notShowClose: true,
        disableEscapeKeyPress: true,
      };

      this.modalService.activate(modalConfig);
    }

    // First parse the value to the right model type
    let parsedValue = this.parse(prop, value);

    // If the parse function expect a number and receives non-numeric input,
    // it returns null, causing the validation service to fail the required rule.
    // If the value parsed to null but was originally not empty, we set the
    // parsedValue back to the entered value.
    if (!isEmpty && parsedValue === null) {
      parsedValue = value;
    }

    // Now validate against rules, validators in the model
    const isValid = await this.validateProp(prop, parsedValue);

    if (isValid || (this.questionItem.optional && isEmpty)) {
      this.informationModalService.showMessageIfPresent(prop, parsedValue);
      this.questionItem.needsFixed = false;

      // TODO: HL-53668 - Remove check for isCoverpathPolicy when implementing for MMDirect/LifeBridge
      if (parsedValue && primaryPolicy.isCoverpathPolicy() && this.piiService.isPiiProperty(this.prop)) {
        const piiId = await this.piiService.setPiiByProperty(this.prop, parsedValue);
        parsedValue = piiId;
      }

      this.submitValueAndFireTriggers(prop, parsedValue);
      // Move this to individual property trigger methods as part of HL-49932: Remove Instances of isTeleCMIEnabled feature flag
      if (
        (prop.match('insured.person.dob') ||
          prop.match('faceAmount') ||
          prop.match('cashValueTransfer') ||
          prop.match('additionalLifeInsurancePremiumBase') ||
          prop.match('rtrRiderAmount') ||
          prop.match('alir1035ExchangeAmount')) &&
        this.appSvc.appState.controller.isTeleCMIEnabled &&
        primaryPolicy.hasTeleCMIMetadata()
      ) {
        const previouslyTeleCMI: boolean = application.isTeleCMI === true;
        const willRequireTeleCMI: boolean = primaryPolicy.isTeleCMIRequired() === true;
        const isTeleCMIOptedIn: boolean = application.isTeleCMIOptedIn === true;

        // Going from Tele required to not required, or vise versa
        // In the case when it is going from not required to required -- we also want to consider the case when tele CMI is opted in
        if (previouslyTeleCMI !== willRequireTeleCMI || isTeleCMIOptedIn === willRequireTeleCMI) {
          // when tele CMI is opted in, we want it to be opted in when we move from "tele not required" to "tele required" and then back to "tele not required"
          application.isTeleCMI = isTeleCMIOptedIn ? application.isTeleCMIOptedIn : primaryPolicy.isTeleCMIRequired();
          // resetting these values when app is non tele CMI
          if (!application.isTeleCMI) {
            (application as CasApplication).teleCMIModeOfContact = 'email';
            (application as CasApplication).isTeleCMILanguageEnglish = true;
            (application as CasApplication).teleCMIReason = null;
            (application as CasApplication).teleCMIReasonOther = null;
          }

          //reset teleCMIReason and teleCMIReasonOther when they are not visible
          if (willRequireTeleCMI) {
            (application as CasApplication).teleCMIReasonOther = null;
            (application as CasApplication).teleCMIReason = null;
          }

          this.updateProgressAndSubscriptions();

          // Show a toast message in the advisor site for either case
          if (this.isAdvisor) {
            const title: string = willRequireTeleCMI
              ? 'Based on the change you made, this case will require a Tele-CMI.'
              : 'Based on the change you made, this case will not require a Tele-CMI.';
            this.toastService.showCustomToast({
              title,
              options: {
                intent: ToasterIntent.INFO,
                intentIcon: ToasterIntent.INFO,
                hasClose: true,
                duration: 10000,
              },
            });
            // Show a toast message if the client site only when switching from required to not required
            // and questions in previous sections now need to be completed
          } else if (!willRequireTeleCMI) {
            const currentSection: number = this.navigationService.currentSection;

            if (!this.appSvc.appState.previousSectionsComplete(currentSection)) {
              this.toastService.showCustomToast({
                title: 'Based on the change you made, some questions in previous sections need to be answered.',
                options: {
                  intent: ToasterIntent.INFO,
                  hasClose: true,
                  toasterType: ToastBySubjectTypes.TELE_MISSED_QUESTION,
                  customTitleLink: {
                    action: () => this.appSvc.teleMissedQuestionSubject.next(),
                    label: 'Take me there',
                  },
                },
              });
            }
          }
        }
      }
      this.updateProgressAndSubscriptions();

      if (this.piiService.isPiiProperty(this.prop)) {
        this.appSvc.obfuscatePiiSubject.next({
          type: this.piiService.getPiiDataElement(this.prop),
          prop: this.prop,
          shouldObfuscate: true,
        });
      }

      if (
        /gender|dob|waiver|state|term|faceAmount|paymentFrequency|dividendOption|loanInterestType|termDisplay|hasEcp|hasGirRider|girRiderAmount|hasRtrRider|rtrRiderAmount|hasAlirRider|additionalLifeInsuranceAmount|hasCirRider|cirRiderAmount|benefitDisbursementMethod/.test(
          prop.split('.').slice(-1).join()
        )
      ) {
        this.appSvc.updateAssociatedProperties(prop, String(value)).then(() => {
          SubjectManager.publish('update_waiver');
        });
      }

      if (/state/.test(prop.split('.').slice(-1).join())) {
        this.appSvc.checkSecondaryAddresseeStatus();
      }

      if (prop.match('insured.person.firstName') || prop.match('insured.person.lastName')) {
        SubjectManager.publish('update_insuredName');
      }

      if (isValid && this.errorMessage[prop]) {
        return false;
      }

      // if valid, check for preceeding invalid/blank and display error states
      const currentSection: number = this.navigationService.currentSection;
      const propStr: string = `${this.questionItem.id}_${prop}`;
      SubjectManager.publish(
        'validate_previous',
        this.applicationService.getPreceedingInvalidProps(currentSection, propStr)
      );

      return true;
    } else {
      this.updateProgressAndSubscriptions();
      return false;
    }
  }

  private resetPropForUnsupportedCase(prop): boolean {
    let setValue = false;
    if (this.unsupportedUseCaseNotReset.includes(prop)) {
      setValue = true;
    } else {
      this.submitValueAndFireTriggers(prop, undefined);
      this.updateProgressAndSubscriptions();
    }
    this.updateFromModel();
    return setValue;
  }

  private submitValueAndFireTriggers(prop: string, value) {
    this.appSvc.submitValueAndFireTriggers(prop, value);
  }

  protected resetOnError(prop: string): boolean {
    return this.appSvc.getResetOnError(prop);
  }

  private fireTrigger(prop: string): void {
    this.appSvc.fireTrigger(prop);
  }

  protected parse(prop: string, value): string {
    const result = this.appSvc.parse(prop, value);

    return result;
  }

  protected async isValidProp(prop: string, value): Promise<boolean> {
    const parsedValue = this.parse(prop, value);

    const isValid = await this.validateProp(prop, parsedValue);

    if (!isValid || (isValid && this.errorMessage[prop])) {
      return false;
    } else if (isValid) {
      return true;
    }
  }

  protected checkEmptyAndValidate(prop: string, value) {
    if (this.isAdvisor) {
      return true;
    }

    if (this.errorMessage[this.prop]) {
      return false;
    }

    const isEmpty = value === null || value === undefined || value === '' || value.length === 0;
    const isRequired = this.appSvc.isRequiredOrNotEmptyProperty(prop, this.questionItem);

    if (isEmpty && isRequired) {
      this.errorMessage[prop] = 'Required';
      return false;
    } else if (!isEmpty) {
      const formattedVal = value instanceof Date ? this.formatterSvc.formatUTCDate(value) : value;
      return this.isValidProp(prop, formattedVal);
    } else {
      return true;
    }
  }

  private async validateProp(prop: string, value): Promise<boolean> {
    this.errorMessage[prop] = '';

    let options;
    if (
      this.questionItem.section === 'Existing Policies' &&
      this.windowService.nativeWindow.controller.enableNewReplacementFeature
    ) {
      options = {
        prop: prop,
        index: this.loopIndex,
        section: this.questionItem.section,
        appState: this.appSvc.appState,
      };
    }

    const metadataValidation: string = this.questionItem.getFullValidationString(prop);
    const result = await this.appSvc.runBusinessValidation(prop, value, metadataValidation, options);
    if (
      result[ValidationService.RESULT] &&
      result[ValidationService.ERR_MSG] &&
      this.windowService.nativeWindow.controller.enableNewReplacementFeature
    ) {
      this.errorMessage[prop] = result[ValidationService.ERR_MSG];
      this.overrideClear = true;
      return true;
    } else if (result[ValidationService.UNSUPPORTED_CASE]) {
      if (result[ValidationService.UNSUPPORTED_CASE_DATA]) {
        this.unsupportedUseCasesService.activateUnsupportedUseCasesModal(
          result[ValidationService.UNSUPPORTED_CASE],
          result[ValidationService.UNSUPPORTED_CASE_DATA]
        );
      } else {
        this.unsupportedUseCasesService.activateUnsupportedUseCasesModal(result[ValidationService.UNSUPPORTED_CASE]);
      }
      this.resetPropForUnsupportedCase(prop);
      return false;
    } else if (!result[ValidationService.RESULT]) {
      this.errorMessage[prop] = result[ValidationService.ERR_MSG];
      this.questionItem.needsFixed = true;
      return false;
    }

    if (result[ValidationService.WARNING_MSG]) {
      this.warningMessage[prop] = result[ValidationService.WARNING_MSG];
    } else {
      this.clearWarningMessage();
    }

    this.overrideClear = false;

    return true;
  }

  protected deriveFormatter(property) {
    if (!property) {
      property = this.questionItem.prop;
    }
    return super.deriveFormatter(property);
  }

  getValuesFor(prop: string) {
    // Use responses_allowed from the metadata if set, otherwise fall back to model
    const responsesAllowed = this.questionItem.responsesAllowed;
    if (responsesAllowed) {
      return responsesAllowed.map((item) => item.value);
    }

    return this.appSvc.getValuesFor(prop);
  }
  getDescriptionsFor(prop: string) {
    // Use responses_allowed from the metadata if set, otherwise fall back to model
    const responsesAllowed = this.questionItem.responsesAllowed;
    if (responsesAllowed) {
      return responsesAllowed.reduce((descriptions, item) => {
        descriptions[`${item.value}`] = item.description;
        return descriptions;
      }, {});
    }

    return this.appSvc.getDescriptionsFor(prop);
  }

  getCustomProp(index: number) {
    return `${this.questionItem.prop}.${this.questionItem.customProps[index]}`;
  }

  getPlaceholder(index: number = 0): string {
    if (!this.questionItem.placeholder) {
      return null;
    }
    const placeholderStr: string[] = this.questionItem.placeholder.split(',');
    return placeholderStr[index];
  }

  getValue(prop: string) {
    return this.appSvc.getValue(prop);
  }

  updateProgressAndSubscriptions() {
    this.appSvc.appState.buildEnabledQuestions();
    this.appSvc.questionAnsweredSubject.next();
  }

  showErroMessage(error) {
    this.errorMessage[this.prop] = error;
  }

  protected getEntityBaseReference(questionItem: CasMetadataNode): object {
    let entity: any = this.appSvc.appState.controller;
    questionItem.entity.split('.').map((subkey) => {
      if (subkey.indexOf('[i]') === -1) {
        //likely a normal string key
        entity = entity[subkey];
      } else {
        //likely an array index
        const arrayName = subkey.replace('[i]', '');
        const arrayRef = entity[arrayName];
        entity = arrayRef[this.loopIndex];
      }
    });
    return entity;
  }

  async setupPiiDisplay(textInput: BasicTextInputComponent) {
    if (this.piiService.isPiiProperty(this.obfuscationProperty.prop)) {
      if (this.appSvc.appState.controller.isLocked) {
        await this.showPii(textInput); //when locked (such as View App), the app is in read-only mode, and need to display SSNs.
      } else {
        this.obfuscatePii(textInput);
      }
    }
  }

  async showPii(textInput: BasicTextInputComponent) {
    const propValue = textInput.val || textInput.getValue(this.obfuscationProperty.prop);
    if (propValue && this.piiService.isPiiProperty(this.obfuscationProperty.prop)) {
      this.shouldWaitForPiiFetch = true;
      const value = await this.piiService.getPiiValueByKey(propValue, this.obfuscationProperty);
      if (value) {
        textInput.val = value;
      }
    }
    if (this.shouldWaitForPiiFetch) {
      textInput.shouldObfuscate = false;
    }
  }

  obfuscatePii(textInput: BasicTextInputComponent) {
    this.shouldWaitForPiiFetch = false;
    const propValue = textInput.val || textInput.getValue(this.obfuscationProperty.prop);
    if (propValue && this.piiService.isPiiProperty(this.obfuscationProperty.prop)) {
      textInput.shouldObfuscate = true;
    } else {
      textInput.shouldObfuscate = false;
    }
  }
}
